import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Configuración base de axios con timeout para cargas grandes
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 10 minutos para operaciones normales
});

// Cache simple para archivos
const fileCache = new Map();
const thumbnailCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
const THUMBNAIL_CACHE_DURATION = 15 * 60 * 1000; // 15 minutos para thumbnails

// Función para obtener cache key
const getCacheKey = (folder = '') => `files_${folder}`;

// Función para verificar si el cache es válido
const isCacheValid = (cacheEntry) => {
  return cacheEntry && (Date.now() - cacheEntry.timestamp) < CACHE_DURATION;
};

// Variables globales para manejar el refresh de tokens (prevenir race conditions)
let isRefreshing = false;
let failedQueue = [];

// Función para procesar la cola de peticiones fallidas después del refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Función helper para extraer email del idToken si no está en sessionStorage
const getEmailFromToken = () => {
  const storedEmail = sessionStorage.getItem('userEmail');
  if (storedEmail) return storedEmail;
  
  // Si no hay email guardado, extraerlo del idToken
  try {
    const idToken = sessionStorage.getItem('cognitoToken');
    if (idToken) {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      // Para Cognito, el email puede estar en email, cognito:username o sub
      const email = payload.email || payload['cognito:username'] || payload.sub;
      if (email) {
        sessionStorage.setItem('userEmail', email);
        return email;
      }
    }
  } catch (error) {
    console.error('Error extrayendo email del token:', error);
  }
  return null;
};

// Interceptor para agregar el token de Cognito en las requests
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('cognitoToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor mejorado para manejo de errores y refresh automático de tokens
// Incluye cola de peticiones para evitar race conditions cuando múltiples peticiones fallan simultáneamente
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Si es un error 401 (Unauthorized) y no es una petición de auth ni ya se intentó refrescar
    if (error.response?.status === 401 && 
        !originalRequest._retry &&
        !originalRequest.url?.includes('/auth/refresh') &&
        !originalRequest.url?.includes('/auth/login') &&
        !originalRequest.url?.includes('/auth/callback') &&
        !originalRequest.url?.includes('/auth/dev-bypass')) {
      
      // Si ya hay un refresh en curso, encolar esta petición para reintentarla después
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        const refreshToken = sessionStorage.getItem('refreshToken');
        const idToken = sessionStorage.getItem('cognitoToken');
        const email = getEmailFromToken(); // Usar función helper para obtener email

        if (!refreshToken || !email) {
          const error = new Error('No hay refresh token o email disponible');
          console.error('❌ Error en interceptor:', error.message);
          processQueue(error, null);
          isRefreshing = false;
          reject(error);
          return;
        }

        // Llamada a refresh usando fetch (para evitar loops con axios)
        // Enviamos también el idToken para que el backend pueda extraer el username correcto
        fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refreshToken, email, idToken }),
        })
          .then(async (res) => {
            const data = await res.json();
            
            if (res.ok && data.success) {
              const newToken = data.tokens.idToken;
              
              // Guardar nuevos tokens
              sessionStorage.setItem('cognitoToken', newToken);
              sessionStorage.setItem('accessToken', data.tokens.accessToken);
              
              // Actualizar el header por defecto de axios para futuras peticiones
              api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
              
              // Actualizar el header de la petición original
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              
              console.log('✅ Token refrescado automáticamente en interceptor - reintentando petición');
              
              // Procesar todas las peticiones encoladas con el nuevo token
              processQueue(null, newToken);
              
              // Reintentar la petición original
              resolve(api(originalRequest));
            } else {
              // Si el refresh falla (token expirado después de 5 días)
              const error = new Error(data.message || 'Refresh token inválido o expirado');
              console.error('❌ Error refrescando token en interceptor:', error.message);
              processQueue(error, null);
              reject(error);
            }
          })
          .catch((err) => {
            console.error('❌ Error de red refrescando token en interceptor:', err);
            processQueue(err, null);
            reject(err);
          })
          .finally(() => { 
            isRefreshing = false; 
          });
      });
    }
    
    // Para otros errores, mantener el comportamiento original
    return Promise.reject(error);
  }
);

// Servicios de autenticación
export const authService = {
  // Login con email y contraseña
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al hacer login: ' + (error.response?.data?.message || error.message));
    }
  },

  // Bypass de desarrollo (solo disponible en desarrollo)
  devBypass: async () => {
    try {
      const response = await api.post('/auth/dev-bypass');
      return response.data;
    } catch (error) {
      throw new Error('Error en bypass de desarrollo: ' + (error.response?.data?.message || error.message));
    }
  },

  // Completar desafío MFA
  completeMFA: async (session, mfaCode, email) => {
    try {
      const response = await api.post('/auth/mfa', {
        session,
        mfaCode,
        email
      });
      return response.data;
    } catch (error) {
      throw new Error('Error en MFA: ' + (error.response?.data?.message || error.message));
    }
  },

  // Refrescar token
  refreshToken: async (refreshToken, email) => {
    try {
      const response = await api.post('/auth/refresh', {
        refreshToken,
        email
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al refrescar token: ' + (error.response?.data?.message || error.message));
    }
  }
};

// Servicios para archivos
export const fileService = {
    // Obtener lista de archivos (con filtro opcional por carpeta) - con caché
  getFiles: async (folder = '', forceRefresh = false) => {
    try {
      const cacheKey = getCacheKey(folder);
      const cachedData = fileCache.get(cacheKey);
      
      // Si se fuerza refrescar o no hay datos en caché válidos, obtener datos frescos
      if (forceRefresh || !isCacheValid(cachedData)) {
        const params = folder ? { folder } : {};
        const response = await api.get('/files', { params });
        
        // Extraer solo los archivos de la nueva estructura de respuesta
        const files = response.data.files || response.data || [];
        
        // Guardar en caché
        fileCache.set(cacheKey, {
          data: files,
          timestamp: Date.now()
        });
        
        return { data: files, fromCache: false };
      }
      
      // Usar datos en caché si son válidos
      return { data: cachedData.data, fromCache: true };
    } catch (error) {
      // Si hay un error pero tenemos datos en caché (aunque sean viejos), usarlos
      const cacheKey = getCacheKey(folder);
      const cachedData = fileCache.get(cacheKey);
      if (cachedData) {
        return { data: cachedData.data, fromCache: true };
      }
      
      throw new Error('Error al obtener archivos: ' + error.message);
    }
  },

  // Función para limpiar caché
  clearCache: (folder = null) => {
    if (folder) {
      const cacheKey = getCacheKey(folder);
      fileCache.delete(cacheKey);
    } else {
      fileCache.clear();
    }
  },

  // Función para limpiar caché de thumbnails
  clearThumbnailCache: (fileName = null) => {
    if (fileName) {
      // Limpiar thumbnails específicos de un archivo
      const keysToDelete = [];
      for (const key of thumbnailCache.keys()) {
        if (key.includes(fileName)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => thumbnailCache.delete(key));
    } else {
      thumbnailCache.clear();
    }
  },

  // Obtener estadísticas
  getStats: async () => {
    try {
      const response = await api.get('/files/stats', {
        params: { useCache: 'false' }
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener estadísticas: ' + error.message);
    }
  },

  // Obtener solo las carpetas raíz
  getRootFolders: async () => {
    try {
      const response = await api.get('/files/folders');
      const folders = response.data.folders || [];
      return folders;
    } catch (error) {
      throw new Error('Error al obtener carpetas raíz: ' + error.message);
    }
  },

  // Buscar archivos
  searchFiles: async (query, fileType = '', minSize = 0, maxSize = Number.MAX_SAFE_INTEGER, searchAll = true) => {
    try {
      const response = await api.get('/files/search', {
        params: { 
          query, 
          fileType, 
          minSize, 
          maxSize,
          searchAll: searchAll.toString()
        }
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al buscar archivos: ' + error.message);
    }
  },

  // Obtener URL de archivo
  getFileUrl: async (fileName, download = false) => {
    try {
      const response = await api.get('/files/url', {
        params: { key: fileName, ...(download ? { download: true } : {}) }
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener URL: ' + error.message);
    }
  },

  // Crear carpeta
  createFolder: async (folderName, tags = null) => {
    try {
      const body = {
        folderName
      };
      // Agregar tags solo si se proporcionan
      if (tags && Array.isArray(tags) && tags.length > 0) {
        body.tags = tags;
      }
      
      const response = await api.post('/files/folder', body);
      
      // Limpiar caché relacionado
      const folderPath = folderName.split('/').slice(0, -1).join('/');
      fileService.clearCache(folderPath);
      
      return response.data;
    } catch (error) {
      throw new Error('Error al crear carpeta: ' + error.message);
    }
  },

  // Subir archivo base64
  uploadBase64: async (fileName, base64Data, mimeType, tags = null, onUploadProgress = null) => {
    const body = {
      fileName,
      base64Data,
      mimeType
    };
    // Agregar tags solo si se proporcionan
    if (tags && Array.isArray(tags) && tags.length > 0) {
      body.tags = tags;
    }
    
    const config = {};
    let processingInterval = null;
    let uploadFinished = false;
    
    if (onUploadProgress) {
      let startTime = Date.now();
      
      config.onUploadProgress = (progressEvent) => {
        if (progressEvent.total) {
          // El progreso del envío HTTP (50-90%, ya que 0-50% es la conversión)
          // Dejamos 90-100% para el procesamiento en el servidor
          const uploadProgress = (progressEvent.loaded / progressEvent.total) * 40; // 40% en lugar de 50%
          const totalProgress = 50 + uploadProgress; // 50% (conversión) + hasta 40% (envío) = máximo 90%
          
          // Calcular tiempo restante basado en velocidad
          const elapsed = (Date.now() - startTime) / 1000; // segundos
          if (elapsed > 0 && progressEvent.loaded > 0) {
            const rate = progressEvent.loaded / elapsed; // bytes por segundo
            const remaining = rate > 0 ? (progressEvent.total - progressEvent.loaded) / rate : 0;
            onUploadProgress(totalProgress, remaining);
          } else {
            onUploadProgress(totalProgress, 0);
          }
          
          // Cuando el envío HTTP termine, comenzar simulación de procesamiento
          if (progressEvent.loaded === progressEvent.total && !uploadFinished) {
            uploadFinished = true;
            // Mostrar 90% y comenzar progreso gradual hasta 95% mientras procesa el servidor
            onUploadProgress(90, 0);
            
            let processingProgress = 90;
            processingInterval = setInterval(() => {
              if (processingProgress < 95) {
                processingProgress += 0.3; // Incremento más lento
                onUploadProgress(processingProgress, 0);
              } else {
                if (processingInterval) {
                  clearInterval(processingInterval);
                  processingInterval = null;
                }
              }
            }, 1000); // Actualizar cada segundo
          }
        }
      };
    }
    
    try {
      const response = await api.post('/files/upload/base64', body, config);
      
      // Limpiar intervalo de procesamiento si existe
      if (processingInterval) {
        clearInterval(processingInterval);
        processingInterval = null;
      }
      
      // Cuando la respuesta llegue, mostrar 100%
      if (onUploadProgress) {
        onUploadProgress(100, 0);
      }
      
      // Limpiar caché relacionado
      const folderPath = fileName.split('/').slice(0, -1).join('/');
      fileService.clearCache(folderPath);
      
      return response.data;
    } catch (error) {
      // Limpiar intervalo de procesamiento si existe en caso de error
      if (processingInterval) {
        clearInterval(processingInterval);
        processingInterval = null;
      }
      throw new Error('Error al subir archivo: ' + error.message);
    }
  },

  // Eliminar archivo individual
  deleteFile: async (fileName) => {
    try {
      const response = await api.delete('/files/delete', {
        params: { key: fileName }
      });
      
      // Limpiar caché relacionado
      const folderPath = fileName.split('/').slice(0, -1).join('/');
      fileService.clearCache(folderPath);
      
      return response.data;
    } catch (error) {
      throw new Error('Error al eliminar archivo: ' + error.message);
    }
  },

  // Eliminar múltiples archivos
  deleteMultipleFiles: async (fileNames) => {
    try {
      const response = await api.delete('/files/delete/multiple', {
        data: { keys: fileNames }
      });
      
      // Limpiar caché para todas las carpetas afectadas
      const affectedFolders = [...new Set(fileNames.map(fileName => 
        fileName.split('/').slice(0, -1).join('/')
      ))];
      affectedFolders.forEach(folderPath => fileService.clearCache(folderPath));
      
      return response.data;
    } catch (error) {
      throw new Error('Error al eliminar archivos: ' + error.message);
    }
  },

  // Eliminar carpeta
  deleteFolder: async (folderPath) => {
    try {
      const response = await api.delete('/files/delete/folder', {
        params: { path: folderPath }
      });
      
      // Limpiar caché relacionado
      const parentFolder = folderPath.split('/').slice(0, -1).join('/');
      fileService.clearCache(parentFolder);
      
      return response.data;
    } catch (error) {
      throw new Error('Error al eliminar carpeta: ' + error.message);
    }
  },

  // Renombrar archivo
  renameFile: async (oldFileName, newFileName) => {
    try {
      const response = await api.post('/files/rename', {
        oldFileName,
        newFileName
      });
      
      // Limpiar caché relacionado
      const oldFolder = oldFileName.split('/').slice(0, -1).join('/');
      const newFolder = newFileName.split('/').slice(0, -1).join('/');
      fileService.clearCache(oldFolder);
      if (oldFolder !== newFolder) {
        fileService.clearCache(newFolder);
      }
      
      return response.data;
    } catch (error) {
      throw new Error('Error al renombrar archivo: ' + error.message);
    }
  },

  // Obtener contenido de archivo de texto
  getFileContent: async (fileName) => {
    try {
      const response = await api.get('/files/content', {
        params: { key: fileName }
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener contenido del archivo: ' + error.message);
    }
  },

  // Obtener thumbnail de una imagen con caché
  getThumbnail: async (fileName, width = 200, height = 150, quality = 50) => {
    try {
      const cacheKey = `thumb_${fileName}_${width}x${height}_q${quality}`;
      const cachedThumbnail = thumbnailCache.get(cacheKey);
      
      // Verificar caché de thumbnails
      if (cachedThumbnail && (Date.now() - cachedThumbnail.timestamp) < THUMBNAIL_CACHE_DURATION) {
        return cachedThumbnail.data;
      }
      const response = await api.get('/files/thumbnail', {
        params: { 
          key: fileName,
          width,
          height,
          quality
        }
      });
      
      // Guardar en caché de thumbnails
      thumbnailCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
      
      return response.data;
    } catch (error) {
      // Si el error indica que el archivo no existe, intentar limpiar el caché
      if (error.response?.data?.suggestion) {
        console.warn('Archivo no encontrado, limpiando caché...');
        try {
          await fileService.clearCacheForMissingFile(fileName);
        } catch (cacheError) {
          console.warn('Error limpiando caché:', cacheError);
        }
      }
      throw new Error('Error al obtener thumbnail: ' + error.message);
    }
  },

  // Limpiar caché para archivos faltantes
  clearCacheForMissingFile: async (fileName) => {
    try {
      const response = await api.post('/cache/clear-missing-files', {
        key: fileName
      });
      console.log('✅ Caché limpiado para archivo faltante:', fileName);
      return response.data;
    } catch (error) {
      throw new Error('Error limpiando caché: ' + error.message);
    }
  },

  // Limpiar caché específico
  clearCache: async (folder = null) => {
    try {
      const response = await api.post('/cache/clear', {
        folder
      });
      console.log('✅ Caché limpiado:', folder || 'todo el caché');
      return response.data;
    } catch (error) {
      throw new Error('Error limpiando caché: ' + error.message);
    }
  },

  // Refrescar caché
  refreshCache: async () => {
    try {
      const response = await api.post('/cache/refresh');
      console.log('✅ Caché refrescado');
      return response.data;
    } catch (error) {
      throw new Error('Error refrescando caché: ' + error.message);
    }
  },

  // Obtener tags de un archivo
  getFileTags: async (fileName) => {
    try {
      const response = await api.get('/files/tags', {
        params: { key: fileName }
      });
      return response.data.tags || [];
    } catch (error) {
      throw new Error('Error al obtener tags: ' + error.message);
    }
  },

  // Actualizar tags de un archivo
  updateFileTags: async (fileName, tags) => {
    try {
      const response = await api.post(`/files/tags?key=${encodeURIComponent(fileName)}`, {
        tags
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al actualizar tags: ' + error.message);
    }
  },

  // Eliminar tags de un archivo
  deleteFileTags: async (fileName) => {
    try {
      const response = await api.delete('/files/tags', {
        params: { key: fileName }
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al eliminar tags: ' + error.message);
    }
  },

  // Actualizar tags de múltiples archivos
  updateMultipleFileTags: async (fileNames, tags, mode = 'replace') => {
    try {
      const response = await api.post('/files/tags/batch', {
        files: fileNames,
        tags,
        mode
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al actualizar tags: ' + error.message);
    }
  },

  // Obtener catálogo de tags
  getTagsCatalog: async (limit = 50, prefix = '') => {
    try {
      const response = await api.get('/tags', {
        params: {
          limit,
          prefix
        }
      });
      return response.data.tags || [];
    } catch (error) {
      throw new Error('Error al obtener catálogo de tags: ' + error.message);
    }
  },

  // Obtener archivos asociados a un tag
  getFilesByTag: async (tag, limit = 200, prefix = '') => {
    try {
      const response = await api.get('/tags/files', {
        params: {
          tag,
          limit,
          prefix
        }
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener archivos por tag: ' + error.message);
    }
  },

  // Obtener tags predefinidos
  getPredefinedTags: async () => {
    try {
      const response = await api.get('/tags/predefined');
      return response.data.tags || [];
    } catch (error) {
      throw new Error('Error al obtener tags predefinidos: ' + error.message);
    }
  },

  // Obtener tags predefinidos de carpetas
  getPredefinedFolderTags: async () => {
    try {
      const response = await api.get('/tags/predefined/folders');
      const data = response.data || {};
      // Devolver el objeto completo con category1 y category2
      // Si no vienen category1/category2, usar tags como fallback (compatibilidad)
      return {
        tags: data.tags || [],
        category1: data.category1 || (Array.isArray(data.tags) ? data.tags : []),
        category2: data.category2 || [],
        count: data.count || 0
      };
    } catch (error) {
      console.error('Error obteniendo tags predefinidos de carpetas:', error);
      throw new Error('Error al obtener tags predefinidos de carpetas: ' + error.message);
    }
  },

  // Obtener catálogo de tags de carpetas con conteos
  getFolderTagsCatalog: async () => {
    try {
      const response = await api.get('/tags/folders/catalog');
      return response.data.tags || [];
    } catch (error) {
      throw new Error('Error al obtener catálogo de tags de carpetas: ' + error.message);
    }
  },

  // Obtener carpetas por tag
  getFoldersByTag: async (tag) => {
    try {
      const response = await api.get('/tags/folders', {
        params: { tag }
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener carpetas por tag: ' + error.message);
    }
  },

  // Obtener tags de una carpeta
  getFolderTags: async (folderName) => {
    try {
      const response = await api.get('/folders/tags', {
        params: { key: folderName }
      });
      return response.data.tags || [];
    } catch (error) {
      throw new Error('Error al obtener tags de carpeta: ' + error.message);
    }
  },

  // Actualizar tags de una carpeta
  updateFolderTags: async (folderName, tags) => {
    try {
      const response = await api.post(`/folders/tags?key=${encodeURIComponent(folderName)}`, {
        tags
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al actualizar tags de carpeta: ' + error.message);
    }
  },

  // Compartir archivos temporalmente
  shareFiles: async (fileKeys, password = null, folderName = null) => {
    try {
      // Determinar si es una operación grande que requiere timeout extendido
      const fileCount = fileKeys?.length || 0;
      const estimatedSizeMB = fileCount * 20; // Estimación: 20MB por archivo
      const isLargeOperation = fileCount > 50 || estimatedSizeMB > 1000; // Más de 50 archivos o >1GB
      
      // Usar timeout extendido solo para esta petición específica
      const timeout = isLargeOperation ? 1800000 : 600000; // 30 min para grandes, 10 min para normales
      
      if (isLargeOperation) {
        console.log(`⏱️ [shareFiles] Usando timeout extendido (30 min) para operación grande: ${fileCount} archivos, ~${estimatedSizeMB.toFixed(2)} MB`);
      }
      
      const response = await api.post('/files/share', {
        fileKeys,
        password,
        folderName
      }, {
        timeout: timeout
      });
      return response.data;
    } catch (error) {
      console.error('Error compartiendo archivos:', error);
      throw error.response?.data || error;
    }
  },

  // Compartir carpeta (con validación de reglas)
  shareFolder: async (folderPath, password = null, folderName = null) => {
    try {
      const response = await api.post('/files/share-folder', {
        folderPath,
        password,
        folderName
      });
      return response.data;
    } catch (error) {
      console.error('Error compartiendo carpeta:', error);
      throw error.response?.data || error;
    }
  },

  // Obtener link público (para verificar)
  getPublicShareLink: async (token) => {
    try {
      const response = await api.get(`/public/view/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo link público:', error);
      throw error.response?.data || error;
    }
  },

  // Obtener todas las carpetas temporales del usuario
  getSharedLinks: async () => {
    try {
      const response = await api.get('/files/shared');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo carpetas temporales:', error);
      throw error.response?.data || error;
    }
  },

  // Obtener detalles de una carpeta temporal específica
  getSharedLinkDetails: async (token) => {
    try {
      const response = await api.get(`/files/shared/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo detalles de carpeta temporal:', error);
      throw error.response?.data || error;
    }
  },

  // Agregar archivos a una carpeta temporal existente
  addFilesToSharedLink: async (token, fileKeys) => {
    try {
      const response = await api.post(`/files/shared/${token}/add`, {
        fileKeys
      });
      return response.data;
    } catch (error) {
      console.error('Error agregando archivos a carpeta temporal:', error);
      throw error.response?.data || error;
    }
  },

  // Quitar archivos de una carpeta temporal existente
  removeFilesFromSharedLink: async (token, fileKeys) => {
    try {
      const response = await api.delete(`/files/shared/${token}/remove`, {
        data: { fileKeys }
      });
      return response.data;
    } catch (error) {
      console.error('Error quitando archivos de carpeta temporal:', error);
      throw error.response?.data || error;
    }
  },

  // Eliminar carpeta temporal completa
  deleteSharedLink: async (token) => {
    try {
      const response = await api.delete(`/files/shared/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error eliminando carpeta temporal:', error);
      throw error.response?.data || error;
    }
  }
};

export default api; 